<template>
  <div class="git-import-wrapper" style="width: 100%">
    <header class="mx-2 mb-4">
      <h3 class="title is-6 mb-1">
        <span v-if="!isExistingWorkflow">
          Import a workflow from a Git repository
        </span>
        <span v-else>Import a new version</span>
      </h3>
      <span class="subtitle is-7 mb-1">
        Provide a valid Git repository URL and select a version commit
      </span>
    </header>
    <fieldset :disabled="isImporting">
      <div class="git-import-fields mb-6">
        <div class="field mb-4">
          <label class="label is-size-7 mx-2 mt-1">Repository URL</label>
          <div class="field has-addons m-0">
            <fieldset :disabled="isCheckingRemote">
              <div
                class="control is-expanded"
                :class="{ 'has-icons-right': lastCheckedRemote }"
              >
                <input
                  v-model="repositoryUrl"
                  class="input"
                  :class="{
                    'is-danger': remoteError || v$.repositoryUrl.$invalid,
                  }"
                  type="text"
                  placeholder="Repository Url (e.g. https://github.com/nf-core/rnaseq)"
                  :disabled="lastCheckedRemote && !isExistingWorkflow"
                  @keyup.enter="validateAndCheckRemote"
                  @change="validateAndCheckRemote"
                  @input="repositoryUrl = repositoryUrl.trim()"
                />
                <span v-if="lastCheckedRemote" class="icon is-right">
                  <button class="delete" @click="resetAll" />
                </span>
              </div>
              <div class="control">
                <button
                  class="button is-primary"
                  :class="{
                    'is-outlined': !repositoryUrl || v$.repositoryUrl.$invalid,
                    'is-loading': isCheckingRemote,
                  }"
                  :disabled="v$.repositoryUrl.$invalid"
                  @click="checkRemote"
                >
                  <span class="icon">
                    <i class="fa-solid fa-search"></i>
                  </span>
                </button>
              </div>
            </fieldset>
          </div>
          <div
            v-if="v$.repositoryUrl.$invalid"
            class="help is-danger is-inline mx-2"
          >
            <ul class="is-inline">
              <li
                v-for="(err, index) of v$.repositoryUrl.$errors"
                :key="`err-${index}`"
                class="mr-2 is-inline-block"
              >
                {{ err.$message }}.
              </li>
            </ul>
          </div>
          <div v-else-if="remoteError">
            <p class="help is-danger">Error: {{ remoteError }}</p>
            <p
              v-if="repositoryUrl.startsWith('http')"
              class="help is-primary has-text-weight-bold"
            >
              If your repository is protected, please
              <a
                style="border-bottom: 1px dashed var(--color-link)"
                @click="
                  () => {
                    repositoryUrl = sshRepositoryUrl
                    checkRemote()
                  }
                "
              >
                <span class="icon">
                  <i class="fa-solid fa-exchange-alt"></i>
                </span>
                try the git+ssh notation like '{{ sshRepositoryUrl }}'
              </a>
            </p>
          </div>
          <p
            v-else-if="isExistingWorkflow"
            class="notification is-fullwidth is-info is-light is-size-7 m-0 my-1 p-0"
          >
            <span class="icon">
              <i class="fa-solid fa-info-circle"></i>
            </span>
            <span class="text">
              You already have imported a workflow version for this workflow.
            </span>
          </p>
          <div
            v-if="repositoryUrlWasNormalized"
            class="notification is-info is-light is-fullwidth is-size-7 mt-2"
          >
            <section>
              <p class="title">Repository URL adjusted</p>
              <p class="mb-2">
                LabID will use the repository root. Any branch, tag, commit, or
                page path from the provided URL is not selected automatically;
                choose the required workflow version below.
              </p>
              <p class="repository-url-line">
                <span class="has-text-weight-semibold">Provided:</span>
                <code>{{ trimmedRepositoryInput }}</code>
              </p>
              <p class="repository-url-line">
                <span class="has-text-weight-semibold">Used:</span>
                <code>{{ effectiveRepositoryUrl }}</code>
              </p>
            </section>
          </div>
        </div>
        <div class="field mb-4">
          <label class="label is-size-7 mx-2 mt-1">
            Workflow version commit
          </label>
          <div class="field has-addons m-0">
            <fieldset :disabled="isCheckingRemote">
              <div class="control is-expanded" style="width: 100%">
                <SelectContainer
                  :options="
                    versionSearchQuery
                      ? filteredVersionsOptions
                      : versionOptions
                  "
                  track-by="commitHash"
                  :value="selectedVersionCommit"
                  :multiple="false"
                  :internal-search="false"
                  :disabled="Object.keys(remoteCommits || {}).length === 0"
                  @input="(v) => (selectedVersionCommit = v)"
                  @search-change="(q) => (versionSearchQuery = q)"
                >
                  <template #singleLabel>
                    <div
                      v-if="selectedVersionCommit"
                      class="is-flex is-align-items-center mb-0"
                    >
                      <div class="tags has-addons mb-0">
                        <span
                          class="tag is-primary is-flex is-align-items-center m-0"
                        >
                          <template v-if="selectedVersionCommit.tag">
                            <span class="icon">
                              <i class="fa-solid fa-tag fa-sm"></i>
                            </span>
                            <span class="has-text-weight-bold mx-1 mr-2">
                              {{ selectedVersionCommit.tag }}
                            </span>
                            ({{ selectedVersionCommit.shortCommitHash }})
                          </template>
                          <template v-else>
                            <span class="icon">
                              <i class="fa-solid fa-code-commit fa-sm"></i>
                            </span>
                            <span class="has-text-weight-bold mx-1">
                              {{ selectedVersionCommit.shortCommitHash }}
                            </span>
                          </template>
                        </span>
                      </div>
                      <span
                        class="mx-2"
                        style="overflow: hidden; text-overflow: ellipsis"
                      >
                        {{ selectedVersionCommit.message }}
                      </span>
                    </div>
                  </template>
                  <template #option="{ option }">
                    <span
                      :class="{ 'already-imported': option.alreadyImported }"
                    >
                      <span
                        v-if="option.alreadyImported"
                        class="tag is-success is-small has-text-weight-bold mr-4"
                      >
                        <span
                          v-tooltip="'This version was already imported'"
                          class="icon"
                        >
                          <i class="fa-solid fa-certificate"></i>
                        </span>
                        <span>Imported</span>
                      </span>
                      <span
                        :title="
                          option.tag
                            ? `Tag: ${option.tag}`
                            : `Commit: ${option.shortCommitHash}`
                        "
                        class="tag is-primary is-light is-small is-outlined has-text-weight-bold mr-4"
                      >
                        <span v-if="option.tag" class="icon mr-1">
                          <i class="fa-solid fa-tag fa-sm"></i>
                        </span>
                        <span v-else class="icon mr-1">
                          <i class="fa-solid fa-code-commit fa-sm"></i>
                        </span>
                        {{ option.tag || option.shortCommitHash }}
                      </span>
                      <span class="" style="max-width: 100%">
                        {{ option.message }}
                      </span>
                    </span>
                  </template>
                </SelectContainer>
              </div>
            </fieldset>
          </div>
        </div>
        <div
          v-if="isExistingWorkflowVersion"
          class="notification is-success is-light is-flex is-justify-content-space-between m-0"
        >
          <span class="icon ml-0 mr-4">
            <i class="fa-solid fa-certificate"></i>
          </span>
          <div class="text has-text-left">
            This version has already been imported.
          </div>
          <router-link
            :to="{
              name: 'workflowVersionDetail',
              params: {
                modelType: 'default',
                id: selectedVersion.id,
              },
            }"
          >
            <button class="button is-success is-small">
              <span class="icon">
                <i class="fa-solid fa-forward-step"></i>
              </span>
              <span>
                Go to
                <b>{{ selectedVersion.name }}</b>
              </span>
            </button>
          </router-link>
        </div>
      </div>
      <div v-if="!isExistingWorkflowVersion && isValidUrl && isValidCommit">
        <h3 class="title workflow-title is-5 mx-1 p-2 my-2">
          <span>
            <span class="icon mr-2">
              <i class="fa-solid fa-code-branch"></i>
            </span>
            <span v-if="workflowName">{{ workflowName }}</span>
            <span v-else>Workflow</span>
          </span>
          <span
            v-if="workflowManager"
            class="tag is-primary is-light is-small has-text-weight-bold ml-2"
          >
            {{ workflowManager }}
          </span>
          <span
            v-if="workflowFilesCount > 0"
            class="tag is-primary is-light is-small has-text-weight-bold ml-2"
          >
            <span class="icon">
              <i class="fa-solid fa-file-code"></i>
            </span>
            {{ workflowFilesCount }}
          </span>
        </h3>
        <fieldset :disabled="isLoadingRepositoryFiles">
          <div class="git-import-fields px-3">
            <slot name="workflowName" :can-change="!isExistingWorkflow" />
            <div v-if="!isLoadingRepositoryFiles" class="field widefield my-2">
              <label class="label form-label">Workflow Files Information</label>
              <p class="help is-size-7 mx-2 mb-4">
                Add and type workflow files for this workflow version. Workflow
                files are the essential set of files that define the workflow,
                describe it or are necessary for its execution.
              </p>
              <WorkflowFiles
                :workflow-files="workflowFiles"
                :file-options="remoteFileOptions"
                :is-edit-mode="!isExistingWorkflowVersion"
                :can-delete-files="true"
                :can-restore-files="false"
                :type-options="typeOptions"
                @change-file="handleChangeFile"
                @change-file-type="handleChangeFile"
                @delete-file="handleDeleteFile"
              />
              <div style="display: inline-block">
                <!-- <Popover> -->
                <button
                  v-tooltip="
                    `Add a file from the repository to the list of workflow 
                      files for this workflow version.`
                  "
                  class="button is-small is-primary is-outlined"
                  :disabled="remoteFileOptions.length === 0"
                  @click="handleAddNextFile()"
                >
                  <span class="icon">
                    <i class="fa-solid fa-plus" />
                  </span>
                </button>
              </div>
            </div>
            <div
              v-else-if="
                selectedVersionCommit &&
                selectedVersionCommit.shortCommitHash &&
                isLoadingRepositoryFiles
              "
              class="is-flex is-justify-content-center is-align-items-center my-4"
            >
              <span class="has-text-primary has-text-weight-bold">
                <span class="icon is-large">
                  <span class="fa-solid fa-spinner fa-spin"></span>
                </span>
                {{ selectedVersionCommit.shortCommitHash }}
              </span>
            </div>
          </div>
          <div
            v-if="!isLoadingRepositoryFiles"
            class="is-flex is-justify-content-flex-end is-align-items-flex-end my-4 mr-2"
          >
            <button
              class="button is-primary"
              :class="{ 'is-outlined': !canImport, 'is-loading': isImporting }"
              :disabled="!canImport"
              @click="importWorkflow"
            >
              <span class="icon">
                <i class="fa-solid fa-cloud-download" />
              </span>
              <span>
                Import Version
                {{
                  selectedVersionCommit.tag ||
                  selectedVersionCommit.shortCommitHash
                }}
              </span>
            </button>
          </div>
        </fieldset>
      </div>
    </fieldset>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { helpers, or, required, url } from '@vuelidate/validators'
import { compareVersions } from 'compare-versions'
import { v4 as uuid } from 'uuid'
import { computed, onMounted, ref, watch, watchEffect } from 'vue'
import { useRouter } from 'vue-router'

import { get, post, put } from '@/api'
import { useRemoteRepositoryFiles } from '@/composables/useRemoteRepositoryFiles'
import { WORKFLOW_DATATYPES } from '@/constants'
import { useStore } from '@/store'
import { normalizeRepositoryUrl } from '@/utils/gitRepositoryUrl'

import SelectContainer from '@/components/SelectContainer.vue'
import WorkflowFiles from '@/components/widget/WorkflowFiles.vue'

export default {
  name: 'GitWorkflowImport',
  components: {
    SelectContainer,
    WorkflowFiles,
  },
  props: {
    appName: { type: String, required: true },
    modelName: { type: String, required: true },
    modelType: { type: String, required: true },
    workflowData: { type: Object },
    workflowName: { type: String },
  },
  setup(props, { emit }) {
    const store = useStore()
    const router = useRouter()
    const typeOptions = WORKFLOW_DATATYPES

    /* Existing workflow info from parent */
    const workflow = ref({})
    watch(
      () => props.workflowData,
      (data) => (workflow.value = data || {}),
      { immediate: true },
    )
    watch(
      () => workflow.value?.name,
      (n) => emit('update-name', n),
      { immediate: true },
    )

    const workflowId = computed(() => workflow.value?.id)
    const isExistingWorkflow = computed(() => !!workflowId.value)
    const workflowVersions = computed(
      () => workflow.value?.workflowversions || [],
    )
    const workflowVersionsCommits = computed(() =>
      workflowVersions.value.map((v) => v.commit_hash),
    )
    const workflowVersionExists = (commitHash) =>
      (workflowVersionsCommits.value.length > 0 &&
        workflowVersionsCommits.value.includes(commitHash)) ||
      false
    const workflowRemoteUrl = computed(() => workflow.value?.remote_repository)

    /* Remote url of workflow repo, either from parent or inputed by user */
    const repositoryUrl = ref(
      workflowRemoteUrl.value ? workflowRemoteUrl.value : '',
    )

    const trimmedRepositoryInput = computed(() => repositoryUrl.value.trim())
    const shouldNormalizeRepositoryUrl = computed(() => !props.workflowData?.id)

    // Only new workflow registration receives the expanded URL handling.
    const effectiveRepositoryUrl = computed(() =>
      shouldNormalizeRepositoryUrl.value
        ? normalizeRepositoryUrl(trimmedRepositoryInput.value)
        : trimmedRepositoryInput.value,
    )

    const repositoryUrlWasNormalized = computed(
      () =>
        shouldNormalizeRepositoryUrl.value &&
        !!trimmedRepositoryInput.value &&
        trimmedRepositoryInput.value !== effectiveRepositoryUrl.value,
    )

    function resetAll() {
      repositoryUrl.value = ''
      resetRemoteForm()
    }
    /* The version commit to be selected by user */
    const selectedVersionCommit = ref(null)
    const hasSelectedVersion = computed(
      () => !!selectedVersionCommit.value?.commitHash,
    )

    const selectedVersion = computed(() => {
      return (
        workflowVersions.value.find(
          (v) => v.commit_hash === selectedVersionCommitHash.value,
        ) || {}
      )
    })
    const isExistingWorkflowVersion = computed(() => !!selectedVersion.value.id)

    /* Validate repository URL */
    const isGitUrl = helpers.withMessage(
      'Not a valid repository URL (starting with http(s):// or git@)',
      or(
        url,
        helpers.regex(/^git@[a-zA-Z0-9.-]+:(?:[\w.-]+\/)+[\w.-]+(?:\.git)?$/),
      ),
    )
    const v$ = useVuelidate(
      {
        repositoryUrl: { required, isGitUrl },
        selectedVersionCommit: { required },
      },
      {
        repositoryUrl,
        selectedVersionCommit,
      },
      { $autoDirty: true },
    )
    v$.value.$touch()
    const isValidUrl = computed(() => !v$.value.repositoryUrl.$invalid)
    const isValidCommit = computed(
      () => !v$.value.selectedVersionCommit.$invalid,
    )

    /* Check remote repository to get tags and commit from it */
    const checkRemoteData = ref({})
    const remoteError = ref('')
    const isCheckingRemote = ref(false)
    const checkedRemoteSuccess = ref(false)
    const lastCheckedRemote = ref(null)

    function validateAndCheckRemote() {
      if (!v$.value.repositoryUrl.$invalid) {
        checkRemote()
      }
    }

    async function checkRemote() {
      resetRemoteForm()
      repositoryUrl.value = repositoryUrl.value.trim()
      lastCheckedRemote.value = repositoryUrl.value
      if (!isValidUrl.value) return

      let checkRemoteUrl = `${props.appName}/${props.modelName}/check-remote/?`
      const checkRemoteParams = new URLSearchParams({
        remote_repository: effectiveRepositoryUrl.value,
        format: 'json',
        response_format: 'flat',
      }).toString()
      checkRemoteUrl += checkRemoteParams
      if (effectiveRepositoryUrl.value && isValidUrl.value) {
        try {
          isCheckingRemote.value = true
          const response = await get(checkRemoteUrl)
          if (response.status === 200) {
            checkedRemoteSuccess.value = true
            if (!isExistingWorkflow.value) {
              workflow.value = response.data.results.workflow
            }
            checkRemoteData.value = response.data.results
          }
        } catch (e) {
          console.error(e)
          remoteError.value = e?.response?.data?.message || e?.message || null
        } finally {
          isCheckingRemote.value = false
        }
      }
    }

    const selectedVersionCommitHash = computed(
      () => selectedVersionCommit.value?.commitHash,
    )

    const {
      remoteFiles,
      workflowManager,
      isLoadingRepositoryFiles,
      resetFiles,
    } = useRemoteRepositoryFiles({
      remoteUrl: effectiveRepositoryUrl,
      commitHash: selectedVersionCommitHash,
    })

    function resetRemoteForm() {
      selectedVersionCommit.value = null
      lastCheckedRemote.value = null
      remoteError.value = null
      checkRemoteData.value = {}
      checkedRemoteSuccess.value = false
      resetFiles()
    }

    watch(
      repositoryUrl,
      (repoUrl) => repoUrl !== lastCheckedRemote.value && resetRemoteForm(),
    )

    const remoteCommits = computed(() => {
      const commits = {}
      const remoteCommits = checkRemoteData.value.commits || {}
      const remoteCommitEntries = Object.entries(remoteCommits)
      remoteCommitEntries.forEach(([commitHash, commitInfo]) => {
        const shortCommitHash = commitHash.slice(0, 7)
        const commitData = { commitHash, shortCommitHash }
        commits[commitHash] = { ...commitInfo, ...commitData }
      })
      return commits
    })

    const remoteTags = computed(() => {
      const tags = {}
      const remoteTags = checkRemoteData.value.tags || {}
      const remoteTagEntries = Object.entries(remoteTags)
      remoteTagEntries.forEach(([tagVersion, tagInfo]) => {
        const commitHash = tagInfo.commit_hash
        const shortCommitHash = commitHash.slice(0, 7)
        const tagData = { tag: tagVersion, commitHash, shortCommitHash }
        tags[tagVersion] = { ...tagInfo, ...tagData }
      })
      return tags
    })

    const versionOptions = computed(() => {
      /* Sort tags first, by latest, then commits */
      let opts = [
        // {
        //   tag: 'v1.0.0',
        //   commitHash: '1234567',
        //   shortCommitHash: '1234567',
        //   message: 'Initial commit',
        // },
        ...Object.values(remoteTags.value).sort((a, b) => {
          try {
            return compareVersions(b.tag, a.tag)
          } catch {
            return 0
          }
        }),
        ...Object.values(remoteCommits.value),
      ].map((vOpt) => ({
        ...vOpt,
        alreadyImported: workflowVersionExists(vOpt.commitHash),
      }))
      return opts
    })

    // replace internal search to search also commit hash / version tag
    const versionSearchQuery = ref('')
    const filteredVersionsOptions = computed(() => {
      if (!versionSearchQuery.value) return versionOptions.value
      const q = versionSearchQuery.value.toLowerCase()
      return versionOptions.value.filter(
        (v) =>
          (v.tag && v.tag.toLowerCase().includes(q)) ||
          v.shortCommitHash.toLowerCase().includes(q) ||
          v.message.toLowerCase().includes(q),
      )
    })

    /* Auto select a commit that is not from an already imported version
     * if possible. Else select the first available commit */
    watch(
      () => versionOptions.value,
      (opts) => {
        let v = opts.find((v) => !workflowVersionExists(v.commitHash))
        // fallback on first version in list
        if (!v && opts.length > 0) v = opts[0]
        selectedVersionCommit.value = v
      },
    )

    watch(selectedVersionCommit, async () => (workflowFiles.value = {}))

    const workflowFilesPaths = computed(() =>
      Object.values(workflowFiles.value).map((f) => f.file_path),
    )
    const remoteFileOptions = computed(() => [
      ...remoteFiles.value.filter(
        (f) => !workflowFilesPaths.value.includes(f.file_path),
      ),
    ])

    const workflowFiles = ref({})
    const workflowFilesCount = computed(
      () => Object.keys(workflowFiles.value).length,
    )

    // Populate suggested workflow files only when remoteFiles changes (new commit selected)
    // Using watch instead of watchEffect to avoid regenerating on every remoteFileOptions change
    watch(
      remoteFiles,
      (files) => {
        if (!files || files.length === 0) {
          workflowFiles.value = {}
          return
        }
        const suggestedWorkflowFiles = files.reduce(
          (wfFiles, { file_path, data_type }) => {
            if (data_type?.value?.toLowerCase() !== 'other') {
              const id = uuid()
              wfFiles[id] = { id, file_path, data_type }
            }
            return wfFiles
          },
          {},
        )
        workflowFiles.value = suggestedWorkflowFiles
      },
      { immediate: true },
    )

    function handleAddFile(file) {
      // TODO: check if file is a valid option in options.
      if (file && file.file_path) {
        const id = uuid()
        file = { ...file, id }
        workflowFiles.value[id] = file
      }
    }
    function handleAddNextFile() {
      if (remoteFileOptions.value.length === 0) return
      const nextFile = remoteFileOptions.value?.[0]
      handleAddFile(nextFile)
    }
    function handleChangeFile({ file }) {
      if (!workflowFiles.value[file.id]) return
      workflowFiles.value[file.id].file_path = file.file_path
      workflowFiles.value[file.id].data_type = file.data_type
    }
    function handleDeleteFile({ file }) {
      delete workflowFiles.value[file.id]
    }

    function getRepoName() {
      /// match https://github.com/nf-core/bacass.git as well as git@github.com:nf-core/bacass.git
      /// and return nf-core/bacass
      const match = effectiveRepositoryUrl.value?.match(
        /(?:\/\/|git@).+?[/:](.+?)(\.git){0,1}?$/,
      )
      const name = match ? match[1] : ''
      return name
    }

    watchEffect(() => {
      if (
        !isExistingWorkflow.value &&
        isValidUrl.value &&
        checkedRemoteSuccess.value
      ) {
        emit('update-name', getRepoName())
      }
    })

    const canImport = computed(
      () =>
        isValidUrl.value &&
        isValidCommit.value &&
        props.workflowName &&
        workflowFilesCount.value > 0,
    )
    const isImporting = ref(false)
    const importVersionPayload = computed(() => {
      const basePayload = !isExistingWorkflow.value
        ? {
            name: props.workflowName,
            remote_repository: effectiveRepositoryUrl.value,
            workflow_manager: workflowManager.value,
          }
        : {
            id: workflowId.value,
          }
      const versionName = selectedVersionCommit.value?.tag
        ? `Version ${selectedVersionCommit.value.tag} (${selectedVersionCommit.value?.shortCommitHash})`
        : `Version ${selectedVersionCommit.value?.shortCommitHash}`
      return {
        results: {
          ...basePayload,
          versions: [
            {
              files: Object.values(workflowFiles.value).map((wff) => ({
                data_type: wff.data_type.value,
                file_path: wff.file_path,
              })),
              name: versionName,
              commit_hash: selectedVersionCommitHash.value,
            },
          ],
        },
      }
    })

    async function importWorkflow() {
      const importMethod = !isExistingWorkflow.value ? post : put
      const importBaseUrl = `${props.appName}/${props.modelName}`
      const importVersionUrl = !isExistingWorkflow.value
        ? `${importBaseUrl}/new-from-remote/`
        : `${importBaseUrl}/${workflowId.value}/sync-from-remote/`
      if (repositoryUrl.value && isValidUrl.value) {
        isImporting.value = true
        try {
          const response = await importMethod(
            `${importVersionUrl}?response_format=flat`,
            {
              data: importVersionPayload.value,
            },
          )
          if (response.status === 200 || response.status === 201) {
            store.commit('notify', {
              type: 'success',
              message:
                response?.data?.message || 'Workflow imported successfully',
              isDismissable: true,
            })
            const workflowId = response?.data?.results?.id
            const createdVersion =
              response?.data?.results?.workflowversions.find(
                (v) => v.commit_hash === selectedVersionCommitHash.value,
              )
            if (createdVersion?.id) {
              router.push({
                name: 'workflowVersionDetail',
                params: { modelType: props.modelType, id: createdVersion?.id },
              })
            } else if (workflowId) {
              router.push({
                name: 'workflowDetail',
                params: {
                  modelName: 'workflows',
                  modelType: props.modelType,
                  id: workflowId,
                },
              })
            }
          }
        } catch (e) {
          if (e.response?.status === 400) {
            store.commit('notify', {
              type: 'error',
              message: e.response.data.message,
              isDismissable: true,
            })
          } else {
            console.error(e)
          }
        } finally {
          isImporting.value = false
        }
      }
    }

    function convertToSSH(url) {
      // Validate the input URL
      const parsedUrl = new URL(url)
      if (parsedUrl.protocol !== 'https:') {
        return
      }

      // Convert the HTTPS URL to SSH format
      const hostname = parsedUrl.hostname // Extract hostname
      const username = parsedUrl.pathname.split('/')[1] // Extract username
      const repository = parsedUrl.pathname.split('/').slice(2).join('/') // Extract repository name

      // Construct the SSH URL
      return `git@${hostname}:${username}/${repository}`
    }
    const sshRepositoryUrl = computed(() =>
      convertToSSH(effectiveRepositoryUrl.value),
    )

    onMounted(() => {
      if (repositoryUrl.value && isValidUrl.value) checkRemote()
    })

    return {
      workflow,
      workflowId,
      repositoryUrl,
      sshRepositoryUrl,
      isExistingWorkflow,
      workflowVersionsCommits,
      workflowVersions,
      selectedVersion,
      hasSelectedVersion,
      isExistingWorkflowVersion,
      checkRemote,
      validateAndCheckRemote,
      remoteCommits,
      remoteTags,
      isCheckingRemote,
      lastCheckedRemote,
      remoteError,
      // onSearchChange,
      versionSearchQuery,
      filteredVersionsOptions,
      versionOptions,
      selectedVersionCommit,
      isValidUrl,
      isValidCommit,
      v$,
      resetAll,
      workflowFiles,
      workflowFilesPaths,
      workflowFilesCount,
      workflowManager,
      isLoadingRepositoryFiles,
      remoteFiles,
      remoteFileOptions,
      typeOptions,
      handleAddNextFile,
      handleChangeFile,
      handleDeleteFile,
      canImport,
      isImporting,
      importVersionPayload,
      importWorkflow,
      convertToSSH,
      trimmedRepositoryInput,
      effectiveRepositoryUrl,
      repositoryUrlWasNormalized,
    }
  },
}
</script>

<style lang="scss" scoped>
.git-import-wrapper {
  container-name: git-import-fields;
  container-type: inline-size;

  .title.workflow-title {
    border-bottom: 1px solid var(--brand);
    border-color: var(--brand);
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
  }
  .field.has-addons fieldset {
    display: flex;
    flex: 1;
  }

  .notification.is-success {
    border: 1px solid var(--success);
    padding: 2rem;
  }
}
.already-imported {
  opacity: 0.6;
}
.repository-url-line {
  overflow-wrap: anywhere;
}
.git-import-fields {
  & > :deep(.field:not(.widefield)),
  & > .field:not(.widefield) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.15rem;
    margin-top: 0.2rem;
    margin-bottom: 0.6rem;
  }
}

@include cq(git-import-fields, $cq-sm) {
  .git-import-fields > :deep(.field:not(.widefield)),
  .git-import-fields > .field:not(.widefield) {
    grid-template-columns: clamp(100px, 35%, 220px) minmax(100px, 1fr);
    align-items: first baseline;
    margin-top: 0.4rem;

    & > label {
      grid-column: 1;
    }

    & > :not(label) {
      grid-column: 2;
    }
  }
}

.git-import-fields label {
  white-space: nowrap;
  font-size: 0.75rem;
}
</style>
